import { Plus, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import type { EmailFont, EmailIdentity, EmailStyle } from '../../queries/brandEmailTheme';

const selectClass = 'mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm';
const MAX_RADIUS = 16;
// Must match parseThemeSave (admin-api brand-email-theme-sql.ts).
const MAX_WELCOME_INTRO = 400;
const MAX_WELCOME_FEATURES = 8;
const MAX_FEATURE_LENGTH = 120;

interface StyleFieldsProps {
  style: EmailStyle;
  fonts: EmailFont[];
  canWrite: boolean;
  onChange: (style: EmailStyle) => void;
}

/** Fonts, corner roundness and headline case: what makes a brand's emails look like that brand. */
export function StyleFields({ style, fonts, canWrite, onChange }: StyleFieldsProps) {
  const set = (patch: Partial<EmailStyle>) => onChange({ ...style, ...patch });
  const fontSelect = (key: 'headingFont' | 'bodyFont', label: string, hint: string) => {
    const current = fonts.find((f) => f.key === style[key]);
    return (
      <label className="block text-sm">
        <span className="text-muted-foreground">{label}</span>
        <select
          aria-label={label}
          className={selectClass}
          style={{ fontFamily: current?.stack }}
          value={style[key]}
          disabled={!canWrite}
          onChange={(e) => set({ [key]: e.target.value })}
        >
          {fonts.map((font) => (
            <option key={font.key} value={font.key} style={{ fontFamily: font.stack }}>
              {font.label}
            </option>
          ))}
        </select>
        <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>
      </label>
    );
  };

  return (
    <fieldset className="space-y-3">
      <legend className="mb-2 text-sm font-medium">Style</legend>
      <p className="text-xs text-muted-foreground">
        Emails cannot load web fonts, so a reader without the font installed sees the fallback named next to it.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {fontSelect('headingFont', 'Heading font', 'Headings and the text wordmark.')}
        {fontSelect('bodyFont', 'Body font', 'Everything else.')}
      </div>
      <div className="text-sm">
        <span className="text-muted-foreground">Corner roundness (px)</span>
        <div className="mt-1 flex items-center gap-3">
          <input
            type="range"
            aria-label="Corner roundness slider"
            min={0}
            max={MAX_RADIUS}
            step={1}
            className="flex-1"
            value={style.radius}
            disabled={!canWrite}
            onChange={(e) => set({ radius: Number(e.target.value) })}
          />
          <input
            type="number"
            aria-label="Corner roundness"
            min={0}
            max={MAX_RADIUS}
            className="w-20 rounded-md border border-input bg-background px-2 py-1 text-sm"
            value={Number.isFinite(style.radius) ? style.radius : ''}
            disabled={!canWrite}
            onChange={(e) => set({ radius: e.target.value === '' ? Number.NaN : Number(e.target.value) })}
          />
        </div>
        <span className="mt-1 block text-xs text-muted-foreground">
          The card and panels. Buttons use three quarters of it and pills less. 0 is square.
        </span>
      </div>
      <div role="radiogroup" aria-label="Headline style" className="text-sm">
        <span className="text-muted-foreground">Headline style</span>
        <div className="mt-1 flex gap-4">
          {(
            [
              ['upper', 'Capitals'],
              ['sentence', 'Sentence case'],
            ] as const
          ).map(([value, label]) => (
            <label key={value} className="flex items-center gap-1.5">
              <input
                type="radio"
                name="headlineCase"
                value={value}
                checked={style.headlineCase === value}
                disabled={!canWrite}
                onChange={() => set({ headlineCase: value })}
              />
              {label}
            </label>
          ))}
        </div>
      </div>
    </fieldset>
  );
}

interface WelcomeFieldsProps {
  identity: EmailIdentity;
  canWrite: boolean;
  onChange: (identity: EmailIdentity) => void;
}

/** The welcome email's own copy: the opening sentence and the "Your account provides" list. */
export function WelcomeFields({ identity, canWrite, onChange }: WelcomeFieldsProps) {
  const features = identity.welcomeFeatures ?? [];
  const setFeatures = (next: string[]) => onChange({ ...identity, welcomeFeatures: next });

  return (
    <fieldset className="space-y-3">
      <legend className="mb-2 text-sm font-medium">Welcome email</legend>
      <label className="block text-sm">
        <span className="text-muted-foreground">Intro</span>
        <textarea
          aria-label="Intro"
          className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
          rows={3}
          maxLength={MAX_WELCOME_INTRO}
          value={identity.welcomeIntro ?? ''}
          disabled={!canWrite}
          onChange={(e) => onChange({ ...identity, welcomeIntro: e.target.value })}
        />
        <span className="mt-1 block text-xs text-muted-foreground">
          The sentence after "Welcome to the brand." Leave blank for the general wording. Up to {MAX_WELCOME_INTRO} characters.
        </span>
      </label>
      <div className="text-sm">
        <span className="text-muted-foreground">What the account provides</span>
        <ul className="mt-1 space-y-2">
          {features.map((line, i) => (
            <li key={i} className="flex items-center gap-2">
              <input
                className="flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                aria-label={`Feature line ${i + 1}`}
                maxLength={MAX_FEATURE_LENGTH}
                value={line}
                disabled={!canWrite}
                onChange={(e) => setFeatures(features.map((f, j) => (j === i ? e.target.value : f)))}
              />
              {canWrite && (
                <Button size="icon" variant="ghost" aria-label={`Remove feature line ${i + 1}`} onClick={() => setFeatures(features.filter((_, j) => j !== i))}>
                  <X />
                </Button>
              )}
            </li>
          ))}
        </ul>
        {canWrite && features.length < MAX_WELCOME_FEATURES && (
          <Button size="sm" variant="outline" className="mt-2" onClick={() => setFeatures([...features, ''])}>
            <Plus /> Add a line
          </Button>
        )}
        <span className="mt-1 block text-xs text-muted-foreground">
          Up to {MAX_WELCOME_FEATURES} lines. With none, the email shows a general list.
        </span>
      </div>
    </fieldset>
  );
}
